import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiURl from '../Paths/Api';

const CreateMeeting = () => {
  const [meetingData, setMeetingData] = useState({
    title: '',
    location: '',
    time: '',
    description: '',
    purpose: '',
    attire: 'casual',
    company: '',
    isPublic: 'private',
    capacity: '',
    price: '',
    priceType: 'single', // 'single' or 'tiered'
    image: null,
  });

  const [attendees, setAttendees] = useState([{ name: '', email: '', phone: '', role: '' }]);
  const [csvFile, setCsvFile] = useState(null);
  const [confirmPreview, setConfirmPreview] = useState(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [inputMode, setInputMode] = useState('manual');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [credits, setCredits] = useState(0);
  const [showCreditPopup, setShowCreditPopup] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [isCreditAnimating, setIsCreditAnimating] = useState(false);
  const [customFormFields, setCustomFormFields] = useState([]);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [maxTicketsPerBooking, setMaxTicketsPerBooking] = useState(1);
  const [ticketTiers, setTicketTiers] = useState([
    { id: 1, name: 'Regular', price: '', description: 'Standard Ticket' },
    { id: 2, name: 'Student', price: '', description: 'Student Discount' },
    { id: 3, name: 'VIP', price: '', description: 'VIP Access' }
  ]);
  const navigate = useNavigate();

  // Load Razorpay SDK dynamically
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Clear error/success messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Initialize default form when public meeting is selected
  useEffect(() => {
    if (meetingData.isPublic === 'public') {
      setShowFormBuilder(true);
      // Initialize with default form fields including conditional fields
      if (customFormFields.length === 0) {
        setCustomFormFields([
          {
            id: Date.now(),
            type: 'text',
            label: 'Full Name',
            placeholder: 'Enter your full name',
            required: true,
            fieldName: 'full_name'
          },
          {
            id: Date.now() + 1,
            type: 'email',
            label: 'Email Address',
            placeholder: 'Enter your email',
            required: true,
            fieldName: 'email'
          },
          {
            id: Date.now() + 2,
            type: 'tel',
            label: 'Phone Number',
            placeholder: 'Enter your phone number',
            required: true,
            fieldName: 'phone'
          },
          {
            id: Date.now() + 3,
            type: 'select',
            label: 'Ticket Type',
            placeholder: 'Select ticket type',
            required: true,
            fieldName: 'ticket_type',
            options: ticketTiers.map(tier => tier.name),
            conditionalFields: {
              Student: [
                {
                  type: 'radio',
                  label: 'Student Category',
                  placeholder: 'Select student category',
                  required: true,
                  fieldName: 'student_category',
                  options: ['Regular Student', 'Guest Student'],
                  conditionalFields: {
                    'Regular Student': [
                      {
                        type: 'text',
                        label: 'Student ID',
                        placeholder: 'Enter student ID',
                        required: true,
                        fieldName: 'student_id'
                      }
                    ],
                    'Guest Student': [
                      {
                        type: 'text',
                        label: 'Guest Pass Number',
                        placeholder: 'Enter guest pass number',
                        required: true,
                        fieldName: 'guest_pass_number'
                      },
                      {
                        type: 'select',
                        label: 'Guest Type',
                        placeholder: 'Select guest type',
                        required: true,
                        fieldName: 'guest_type',
                        options: ['Alumni', 'Faculty Guest', 'Industry Guest']
                      }
                    ]
                  }
                },
                {
                  type: 'select',
                  label: 'University',
                  placeholder: 'Select university',
                  required: true,
                  fieldName: 'university',
                  options: ['University of Delhi', 'University of Mumbai', 'University of Calcutta', 'Other']
                }
              ],
              VIP: [
                {
                  type: 'textarea',
                  label: 'Special Requirements',
                  placeholder: 'Any special requirements?',
                  required: false,
                  fieldName: 'special_requirements'
                }
              ]
            }
          }
        ]);
      }
    } else {
      setShowFormBuilder(false);
    }
  }, [meetingData.isPublic, ticketTiers]);

  // Fetch user data and credits from API
  useEffect(() => {
    const fetchUserCredits = async () => {
      try {
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('token');
        const tokenExpiresAt = localStorage.getItem('token_expires_at');

        if (!userId || !token) {
          setError('User not authenticated. Please log in.');
          localStorage.clear();
          navigate('/');
          return;
        }

        if (tokenExpiresAt && new Date(tokenExpiresAt) < new Date()) {
          setError('Session expired. Please log in again.');
          localStorage.clear();
          navigate('/');
          return;
        }

        const response = await fetch(`${ApiURl}/getCredits.php?user_id=${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError('Session invalid. Please log in again.');
            localStorage.clear();
            navigate('/');
            return;
          }
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          const fetchedCredits = parseInt(data.credits) || 0;
          setCredits(fetchedCredits);
          localStorage.setItem('credits', fetchedCredits);
        } else {
          throw new Error(data.message || 'Failed to fetch credits');
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch credits. Please try again later.');
      }
    };

    const storedCompany = localStorage.getItem('company_name') || 'Your Company';
    setMeetingData((prev) => ({ ...prev, company: storedCompany }));
    fetchUserCredits();
  }, [navigate]);

  const handleMeetingChange = (e) => {
    const { name, value, files } = e.target;
    setMeetingData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleAttendeeChange = (index, e) => {
    const { name, value } = e.target;
    const newAttendees = [...attendees];
    newAttendees[index][name] = value;
    setAttendees(newAttendees);
  };

  const addAttendee = () => {
    setAttendees([...attendees, { name: '', email: '', phone: '', role: '' }]);
  };

  const removeAttendee = (index) => {
    setAttendees(attendees.filter((_, i) => i !== index));
  };

  // Form Builder Functions
  const addFormField = (type) => {
    const defaultLabels = {
      text: 'Text Field',
      email: 'Email Address',
      tel: 'Phone Number',
      number: 'Number',
      date: 'Date',
      select: 'Dropdown Selection',
      radio: 'Radio Buttons',
      checkbox: 'Checkbox',
      textarea: 'Long Text'
    };

    const newField = {
      id: Date.now() + Math.random(),
      type,
      label: defaultLabels[type] || 'New Field',
      placeholder: `Enter ${defaultLabels[type]?.toLowerCase() || 'value'}`,
      required: false,
      options: type === 'select' || type === 'radio' ? ['Option 1', 'Option 2'] : [],
      fieldName: `field_${Date.now()}`,
      conditionalFields: (type === 'select' || type === 'radio') ? {} : undefined
    };

    setCustomFormFields([...customFormFields, newField]);
  };

  const updateFormField = (index, updates) => {
    const updatedFields = [...customFormFields];
    updatedFields[index] = { ...updatedFields[index], ...updates };
    setCustomFormFields(updatedFields);
  };

  const removeFormField = (index) => {
    const updatedFields = customFormFields.filter((_, i) => i !== index);
    setCustomFormFields(updatedFields);
  };

  const addOptionToField = (fieldIndex) => {
    const updatedFields = [...customFormFields];
    const field = updatedFields[fieldIndex];
    if (field.options) {
      field.options.push(`Option ${field.options.length + 1}`);
      setCustomFormFields(updatedFields);
    }
  };

  const updateOptionInField = (fieldIndex, optionIndex, value) => {
    const updatedFields = [...customFormFields];
    const field = updatedFields[fieldIndex];
    if (field.options) {
      field.options[optionIndex] = value;
      setCustomFormFields(updatedFields);
    }
  };

  const removeOptionFromField = (fieldIndex, optionIndex) => {
    const updatedFields = [...customFormFields];
    const field = updatedFields[fieldIndex];
    if (field.options && field.options.length > 1) {
      field.options.splice(optionIndex, 1);
      setCustomFormFields(updatedFields);
    }
  };

  // Ticket Tier Functions
  const addTicketTier = () => {
    const newId = ticketTiers.length > 0 ? Math.max(...ticketTiers.map(t => t.id)) + 1 : 1;
    setTicketTiers([
      ...ticketTiers,
      {
        id: newId,
        name: `Tier ${newId}`,
        price: '',
        description: 'New ticket tier',
        conditionalFields: []
      }
    ]);
  };

  const updateTicketTier = (index, updates) => {
    const updatedTiers = [...ticketTiers];
    updatedTiers[index] = { ...updatedTiers[index], ...updates };
    setTicketTiers(updatedTiers);
  };

  const removeTicketTier = (index) => {
    if (ticketTiers.length > 1) {
      const updatedTiers = ticketTiers.filter((_, i) => i !== index);
      setTicketTiers(updatedTiers);
    } else {
      setError('At least one ticket tier is required');
    }
  };

  const addConditionalFieldToTier = (tierIndex) => {
    const updatedTiers = [...ticketTiers];
    const tier = updatedTiers[tierIndex];
    if (!tier.conditionalFields) {
      tier.conditionalFields = [];
    }
    tier.conditionalFields.push({
      id: Date.now() + Math.random(),
      type: 'text',
      label: 'Additional Field',
      placeholder: 'Enter information',
      required: false,
      fieldName: `tier_${tier.id}_field_${tier.conditionalFields.length + 1}`
    });
    setTicketTiers(updatedTiers);
  };

  const updateConditionalField = (tierIndex, fieldIndex, updates) => {
    const updatedTiers = [...ticketTiers];
    const tier = updatedTiers[tierIndex];
    if (tier.conditionalFields && tier.conditionalFields[fieldIndex]) {
      tier.conditionalFields[fieldIndex] = {
        ...tier.conditionalFields[fieldIndex],
        ...updates
      };
      setTicketTiers(updatedTiers);
    }
  };

  const removeConditionalField = (tierIndex, fieldIndex) => {
    const updatedTiers = [...ticketTiers];
    const tier = updatedTiers[tierIndex];
    if (tier.conditionalFields) {
      tier.conditionalFields.splice(fieldIndex, 1);
      setTicketTiers(updatedTiers);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please upload a valid CSV file.');
        return;
      }
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        try {
          const rows = text.split('\n').map(row => row.split(',').map(cell => cell.trim())).filter(row => row.some(cell => cell));
          if (rows.length < 2) {
            setError('CSV file is empty or contains only headers.');
            setCsvFile(null);
            return;
          }
          const headers = rows[0].map(h => h.toLowerCase());
          const requiredHeaders = ['name', 'email', 'phone', 'role'];
          if (!requiredHeaders.every(h => headers.includes(h))) {
            setError('CSV must contain Name, Email, Phone, and Role columns');
            setCsvFile(null);
            return;
          }

          const validRoles = ['user', 'admin', 'supervisor'];
          const data = rows.slice(1).map(row => ({
            name: row[headers.indexOf('name')] || '',
            email: row[headers.indexOf('email')] || '',
            phone: row[headers.indexOf('phone')] || '',
            role: row[headers.indexOf('role')] ? row[headers.indexOf('role').toLowerCase()] : '',
          })).filter(attendee => attendee.name && attendee.email && attendee.phone && attendee.role && validRoles.includes(attendee.role));

          if (data.length === 0) {
            setError('No valid attendee data found in CSV. Ensure roles are "user", "admin", or "supervisor".');
            setCsvFile(null);
            return;
          }

          setConfirmPreview(data);
          setShowConfirmPopup(true);
        } catch (err) {
          setError('Error parsing CSV file. Please ensure it is formatted correctly.');
          setCsvFile(null);
        }
      };
      reader.onerror = () => {
        setError('Error reading CSV file.');
        setCsvFile(null);
      };
      reader.readAsText(file);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "Name,Email,Phone,Role\nJohn Doe,john@example.com,1234567890,user\nJane Smith,jane@example.com,0987654321,admin\nBob Johnson,bob@example.com,9876543210,supervisor";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'attendees_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleAddCredits = async () => {
    const amount = parseInt(creditAmount);
    if (isNaN(amount) || amount < 100) {
      setError('Minimum credit purchase is ₹100 (100 credits)');
      return;
    }

    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');

      if (!userId || !token) {
        setError('User not authenticated. Please log in.');
        localStorage.clear();
        navigate('/');
        return;
      }

      const orderResponse = await fetch(`${ApiURl}/createRazorpayOrder.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userId,
          amount: amount * 100,
        }),
      });

      if (!orderResponse.ok) {
        if (orderResponse.status === 401) {
          setError('Session invalid. Please log in again.');
          localStorage.clear();
          navigate('/');
          return;
        }
        throw new Error(`HTTP error! Status: ${orderResponse.status}`);
      }

      const orderData = await orderResponse.json();
      if (!orderData.success) {
        setError(orderData.message || 'Failed to create payment order');
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'YOUR_RAZORPAY_KEY_ID',
        amount: orderData.amount,
        currency: 'INR',
        name: 'Event Manager',
        description: `Purchase ${amount} credits`,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await fetch(`${ApiURl}/verifyRazorpayPayment.php`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                user_id: userId,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                amount: amount,
              }),
            });

            if (!verifyResponse.ok) {
              if (verifyResponse.status === 401) {
                setError('Session invalid. Please log in again.');
                localStorage.clear();
                navigate('/');
                return;
              }
              throw new Error(`HTTP error! Status: ${verifyResponse.status}`);
            }

            const verifyData = await verifyResponse.json();
            if (verifyData.success) {
              setIsCreditAnimating(true);
              setTimeout(() => setIsCreditAnimating(false), 1000);
              setCredits(verifyData.new_credits);
              localStorage.setItem('credits', verifyData.new_credits);
              setCreditAmount('');
              setShowCreditPopup(false);
              setSuccess(`Added ${amount} credits successfully!`);
            } else {
              setError(verifyData.message || 'Payment verification failed');
            }
          } catch (err) {
            setError('An error occurred while verifying payment. Please try again.');
          }
        },
        prefill: {
          name: localStorage.getItem('full_name') || '',
          email: localStorage.getItem('email') || '',
          contact: localStorage.getItem('phone') || '',
        },
        theme: {
          color: '#0D9488',
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        setError(`Payment failed: ${response.error.description}`);
      });
      razorpay.open();
    } catch (err) {
      setError('An error occurred while initiating payment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmSubmit = async () => {
    setShowConfirmPopup(false);
    await submitForm(confirmPreview, inputMode === 'csv');
  };

  const validateMeetingData = () => {
    const { title, location, time, description, purpose, isPublic, capacity, image, priceType } = meetingData;
    if (!title.trim() || !location.trim() || !time || !description.trim() || !purpose.trim()) {
      setError('All meeting details (Title, Location, Time, Description, Purpose) are required.');
      return false;
    }
    const meetingTime = new Date(time);
    if (meetingTime < new Date()) {
      setError('Meeting time cannot be in the past.');
      return false;
    }
    if (isPublic === 'public' && (!capacity || parseInt(capacity) <= 0)) {
      setError('Capacity must be a positive number for public meetings.');
      return false;
    }
    if (isPublic === 'public' && !image) {
      setError('An event image is required for public meetings.');
      return false;
    }
    if (isPublic === 'public' && priceType === 'single' && (!meetingData.price || parseFloat(meetingData.price) < 0)) {
      setError('Please enter a valid ticket price for single pricing.');
      return false;
    }
    if (isPublic === 'public' && priceType === 'tiered') {
      const invalidTiers = ticketTiers.filter(tier => !tier.price || parseFloat(tier.price) < 0);
      if (invalidTiers.length > 0) {
        setError('Please enter valid prices for all ticket tiers.');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateMeetingData()) return;

    let attendeesData = [];
    if (inputMode === 'csv') {
      if (csvFile && confirmPreview) {
        attendeesData = confirmPreview;
      } else {
        setError('No valid CSV data to submit.');
        return;
      }
    } else {
      attendeesData = attendees.filter(a => a.name && a.email && a.phone && a.role);
    }

    const tempInviteCount = attendeesData.length;
    if (tempInviteCount > credits) {
      setError(`Insufficient credits! You have ${credits} credits, but need ${tempInviteCount} for this meeting.`);
      return;
    }

    setConfirmPreview(attendeesData);
    setShowConfirmPopup(true);
  };

  const submitForm = async (attendeesData, isCsv = false) => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    const userDetails = {
      name: localStorage.getItem('full_name') || '',
      email: localStorage.getItem('email') || '',
      phone: localStorage.getItem('phone') || '',
      role: localStorage.getItem('role') || 'admin',
    };

    let updatedAttendees = attendeesData;
    let inviteCount = attendeesData.length;

    // For private meetings with no attendees, add the creator
    if (meetingData.isPublic !== 'public' && attendeesData.length === 0) {
      updatedAttendees = [userDetails];
      inviteCount = 1;
    }

    const formData = new FormData();
    formData.append('title', meetingData.title);
    formData.append('location', meetingData.location);
    formData.append('time', meetingData.time);
    formData.append('description', meetingData.description);
    formData.append('purpose', meetingData.purpose);
    formData.append('attire', meetingData.attire);
    formData.append('company', meetingData.company);
    formData.append('user_id', localStorage.getItem('userId'));
    formData.append('invite_count', inviteCount);
    formData.append('is_public', meetingData.isPublic === 'public' ? 1 : 0);
    formData.append('capacity', meetingData.isPublic === 'public' ? meetingData.capacity : 0);
    formData.append('price_type', meetingData.priceType);

    if (meetingData.isPublic === 'public') {
      if (meetingData.priceType === 'single') {
        formData.append('price', meetingData.price || 0);
      } else {
        // For tiered pricing, send all tiers as JSON
        formData.append('ticket_tiers', JSON.stringify(ticketTiers));
        formData.append('price', 0); // Set base price as 0
      }

      formData.append('max_tickets_per_booking', maxTicketsPerBooking);
      formData.append('custom_form', JSON.stringify(customFormFields));

      if (meetingData.image) {
        formData.append('image', meetingData.image);
      }
    } else {
      formData.append('price', 0);
    }

    // Send attendees to backend for both public and private meetings
    if (updatedAttendees.length > 0) {
      if (isCsv) {
        formData.append('attendees_csv', csvFile);
      } else {
        formData.append('attendees', JSON.stringify(updatedAttendees));
      }
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('User not authenticated. Please log in.');
        localStorage.clear();
        navigate('/');
        return;
      }

      const response = await fetch(`${ApiURl}/createMeeting.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Session invalid. Please log in again.');
          localStorage.clear();
          navigate('/');
          return;
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        if (inviteCount > 0) {
          setCredits(prev => {
            const newCredits = prev - inviteCount;
            localStorage.setItem('credits', newCredits);
            return newCredits;
          });
        }
        setSuccess('Meeting created successfully!');
        setMeetingData({
          title: '',
          location: '',
          time: '',
          description: '',
          purpose: '',
          attire: 'casual',
          company: meetingData.company,
          isPublic: 'private',
          capacity: '',
          price: '',
          priceType: 'single',
          image: null
        });
        setAttendees([{ name: '', email: '', phone: '', role: '' }]);
        setCsvFile(null);
        setConfirmPreview(null);
        setCustomFormFields([]);
        setTicketTiers([
          { id: 1, name: 'Regular', price: '', description: 'Standard Ticket' },
          { id: 2, name: 'Student', price: '', description: 'Student Discount' },
          { id: 3, name: 'VIP', price: '', description: 'VIP Access' }
        ]);
        setInputMode('manual');
      } else {
        setError(data.message || 'Failed to create meeting');
      }
    } catch (err) {
      setError('An error occurred while creating the meeting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Preview form data
  const [previewFormData, setPreviewFormData] = useState({
    ticket_type: '',
    student_category: ''
  });

  const handlePreviewChange = (fieldName, value) => {
    const newData = {
      ...previewFormData,
      [fieldName]: value
    };

    // Clear dependent fields when parent field changes
    if (fieldName === 'ticket_type') {
      // Clear student category and its dependent fields
      if (newData.student_category) delete newData.student_category;
      if (newData.student_id) delete newData.student_id;
      if (newData.guest_pass_number) delete newData.guest_pass_number;
      if (newData.guest_type) delete newData.guest_type;
    }

    if (fieldName === 'student_category') {
      // Clear student category dependent fields
      if (newData.student_id) delete newData.student_id;
      if (newData.guest_pass_number) delete newData.guest_pass_number;
      if (newData.guest_type) delete newData.guest_type;
    }

    setPreviewFormData(newData);
  };

  // Get conditional fields for selected option
  const getConditionalFields = (field, selectedValue) => {
    if (field.conditionalFields && selectedValue) {
      return field.conditionalFields[selectedValue] || [];
    }
    return [];
  };

  // Get nested conditional fields (for radio buttons within conditional fields)
  const getNestedConditionalFields = (parentField, parentValue, childField, childValue) => {
    if (parentField.conditionalFields &&
      parentField.conditionalFields[parentValue]) {
      const childFieldObj = parentField.conditionalFields[parentValue]
        .find(f => f.fieldName === childField);

      if (childFieldObj &&
        childFieldObj.conditionalFields &&
        childValue) {
        return childFieldObj.conditionalFields[childValue] || [];
      }
    }
    return [];
  };

  // Helper function to find field by fieldName
  const findFieldByName = (fieldName) => {
    return customFormFields.find(f => f.fieldName === fieldName);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans">
      <nav className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-3 shadow-lg fixed w-full z-20">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="mr-3 md:hidden focus:outline-none hover:bg-teal-600 p-2 rounded-full transition-all duration-300"
              aria-label="Toggle Sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="text-lg font-bold tracking-tight">Event Manager</h1>
          </div>
          <button
            onClick={handleLogout}
            className="px-3 py-1.5 bg-red-500 rounded-full hover:bg-red-600 transition-all duration-300 text-sm font-medium shadow-sm hover:scale-105"
            aria-label="Logout"
          >
            Logout
          </button>
        </div>
      </nav>

      <div className="fixed top-16 right-4 z-20">
        <div
          className={`flex items-center bg-white shadow-md rounded-full px-3 py-1 cursor-pointer transition-all duration-300 ${isCreditAnimating ? 'animate-pulse' : 'hover:shadow-lg'}`}
          onClick={() => setShowCreditPopup(true)}
          role="button"
          aria-label={`Current credits: ${credits}`}
        >
          <span className="text-teal-600 font-medium text-sm mr-1">{credits}</span>
          <span className="text-xs text-gray-500 mr-1">credits</span>
          <svg className="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
        </div>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed top-14 left-0 h-[calc(100vh-3.5rem)] bg-gradient-to-b from-teal-600 to-teal-800 text-white w-16 flex flex-col items-center py-4 space-y-4 transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:w-16 z-20`}
      >
        <button
          className="p-2 rounded-full bg-teal-700 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Dashboard"
          onClick={() => navigate('/dashboard')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Dashboard</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Create Meeting"
          onClick={() => navigate('/create-meeting')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Create Meeting</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="View Meetings"
          onClick={() => navigate('/view-meetings')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Reports</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Send Invites"
          onClick={() => navigate('/send-invites')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Invite</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Search Meetings"
          onClick={() => navigate('/search-form')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Search</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Send Invite Without Pay"
          onClick={() => navigate('/adminsend')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Send Invite</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Restrict Meeting"
          onClick={() => navigate('/restrict-meeting')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.104-.896 2-2 2s-2-.896-2-2 2-4 2-4 2 2.896 2 4zm0 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm-6 7c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm12 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4z" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Restrict Meeting</span>
        </button>
      </div>

      <div className="pt-20 md:pl-20 p-4 max-w-6xl mx-auto transition-all duration-500">
        <h1 className="text-xl font-bold text-gray-900 mb-4 animate-fade-in">Create New Meeting</h1>
        <div className="fixed top-24 right-4 bg-teal-100 text-teal-800 p-2 rounded-lg text-xs shadow-md sm:text-sm">
          {meetingData.isPublic === 'public'
            ? 'Public meeting is visible to everyone. Invitations are optional.'
            : 'Private meeting requires at least one invited attendee.'}
        </div>
        {error && (
          <div className="mb-3 p-3 bg-red-50 text-red-600 rounded-lg border border-red-100 text-sm animate-fade-in">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-3 p-3 bg-green-50 text-green-600 rounded-lg border border-red-100 text-sm animate-fade-in">
            {success}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Form - Left Side */}
          <div className="lg:w-2/3">
            <form onSubmit={handleSubmit} className="bg-white p-5 rounded-xl shadow-lg space-y-4 animate-fade-in">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700" htmlFor="title">Event Title <span className="text-red-500">*</span></label>
                  <input
                    id="title"
                    type="text"
                    name="title"
                    value={meetingData.title}
                    onChange={handleMeetingChange}
                    className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700" htmlFor="company">Company Name <span className="text-red-500">*</span></label>
                  <input
                    id="company"
                    type="text"
                    name="company"
                    value={meetingData.company}
                    onChange={handleMeetingChange}
                    className="w-full px-3 py-1.5 bg-gray-100 border border-gray-300 rounded-lg text-sm cursor-not-allowed transition-all duration-300"
                    readOnly
                    required
                    aria-readonly="true"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700" htmlFor="location">Location <span className="text-red-500">*</span></label>
                  <input
                    id="location"
                    type="text"
                    name="location"
                    value={meetingData.location}
                    onChange={handleMeetingChange}
                    className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700" htmlFor="time">Time <span className="text-red-500">*</span></label>
                  <input
                    id="time"
                    type="datetime-local"
                    name="time"
                    value={meetingData.time}
                    onChange={handleMeetingChange}
                    className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700" htmlFor="purpose">Purpose <span className="text-red-500">*</span></label>
                  <input
                    id="purpose"
                    type="text"
                    name="purpose"
                    value={meetingData.purpose}
                    onChange={handleMeetingChange}
                    className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700" htmlFor="attire">Attire</label>
                  <select
                    id="attire"
                    name="attire"
                    value={meetingData.attire}
                    onChange={handleMeetingChange}
                    className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                  >
                    <option value="casual">Casual</option>
                    <option value="business casual">Business Casual</option>
                    <option value="formal">Formal</option>
                    <option value="black tie">Black Tie</option>
                    <option value="theme">Theme-based</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700" htmlFor="isPublic">Meeting Type <span className="text-red-500">*</span></label>
                  <select
                    id="isPublic"
                    name="isPublic"
                    value={meetingData.isPublic}
                    onChange={handleMeetingChange}
                    className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                    required
                    aria-required="true"
                  >
                    <option value="private">Private</option>
                    <option value="public">Public</option>
                  </select>
                </div>
                {meetingData.isPublic === 'public' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700" htmlFor="capacity">Capacity <span className="text-red-500">*</span></label>
                      <input
                        id="capacity"
                        type="number"
                        name="capacity"
                        value={meetingData.capacity}
                        onChange={handleMeetingChange}
                        className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                        min="1"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700" htmlFor="priceType">Pricing Type</label>
                      <select
                        id="priceType"
                        name="priceType"
                        value={meetingData.priceType}
                        onChange={handleMeetingChange}
                        className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                      >
                        <option value="single">Single Price</option>
                        <option value="tiered">Tiered Pricing</option>
                      </select>
                    </div>
                    {meetingData.priceType === 'single' ? (
                      <div>
                        <label className="block text-xs font-medium text-gray-700" htmlFor="price">Ticket Price (₹)</label>
                        <input
                          id="price"
                          type="number"
                          name="price"
                          value={meetingData.price}
                          onChange={handleMeetingChange}
                          className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                          min="0"
                          step="0.01"
                          placeholder="Enter ticket price"
                          aria-label="Ticket Price"
                        />
                      </div>
                    ) : (
                      <div className="sm:col-span-2">
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-xs font-medium text-gray-700">Ticket Tiers</label>
                          <button
                            type="button"
                            onClick={addTicketTier}
                            className="px-2 py-1 bg-teal-500 text-white rounded text-xs hover:bg-teal-600 transition-all"
                          >
                            Add Tier
                          </button>
                        </div>
                        <div className="space-y-3">
                          {ticketTiers.map((tier, index) => (
                            <div key={tier.id} className="border rounded-lg p-3 bg-gray-50">
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Tier Name</label>
                                  <input
                                    type="text"
                                    value={tier.name}
                                    onChange={(e) => updateTicketTier(index, { name: e.target.value })}
                                    className="w-full px-2 py-1 bg-white border rounded text-sm"
                                    placeholder="Tier Name"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Price (₹)</label>
                                  <input
                                    type="number"
                                    value={tier.price}
                                    onChange={(e) => updateTicketTier(index, { price: e.target.value })}
                                    className="w-full px-2 py-1 bg-white border rounded text-sm"
                                    placeholder="0.00"
                                    min="0"
                                    step="0.01"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                                  <input
                                    type="text"
                                    value={tier.description}
                                    onChange={(e) => updateTicketTier(index, { description: e.target.value })}
                                    className="w-full px-2 py-1 bg-white border rounded text-sm"
                                    placeholder="Description"
                                  />
                                </div>
                              </div>

                              {/* Conditional Fields for this tier */}
                              <div className="mt-3">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-xs font-medium text-gray-600">Conditional Fields (show when this tier is selected)</span>
                                  <button
                                    type="button"
                                    onClick={() => addConditionalFieldToTier(index)}
                                    className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-all"
                                  >
                                    Add Field
                                  </button>
                                </div>
                                {tier.conditionalFields && tier.conditionalFields.map((field, fieldIndex) => (
                                  <div key={field.id} className="mb-2 p-2 bg-white border rounded">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                                      <div>
                                        <input
                                          type="text"
                                          value={field.label}
                                          onChange={(e) => updateConditionalField(index, fieldIndex, { label: e.target.value })}
                                          className="w-full px-2 py-1 bg-gray-50 border rounded text-sm"
                                          placeholder="Field Label"
                                        />
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <select
                                          value={field.type}
                                          onChange={(e) => updateConditionalField(index, fieldIndex, { type: e.target.value })}
                                          className="px-2 py-1 bg-gray-50 border rounded text-sm"
                                        >
                                          <option value="text">Text</option>
                                          <option value="email">Email</option>
                                          <option value="tel">Phone</option>
                                          <option value="number">Number</option>
                                          <option value="select">Dropdown</option>
                                          <option value="radio">Radio Buttons</option>
                                        </select>
                                        <button
                                          type="button"
                                          onClick={() => removeConditionalField(index, fieldIndex)}
                                          className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <input
                                        type="text"
                                        value={field.placeholder}
                                        onChange={(e) => updateConditionalField(index, fieldIndex, { placeholder: e.target.value })}
                                        className="flex-1 px-2 py-1 bg-gray-50 border rounded text-sm"
                                        placeholder="Placeholder"
                                      />
                                      <label className="flex items-center text-xs">
                                        <input
                                          type="checkbox"
                                          checked={field.required}
                                          onChange={(e) => updateConditionalField(index, fieldIndex, { required: e.target.checked })}
                                          className="mr-1"
                                        />
                                        Required
                                      </label>
                                    </div>
                                    {/* Options for radio/select fields in conditional fields */}
                                    {(field.type === 'radio' || field.type === 'select') && (
                                      <div className="mt-2">
                                        <div className="flex justify-between items-center mb-1">
                                          <span className="text-xs font-medium">Options:</span>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const updatedTiers = [...ticketTiers];
                                              const tier = updatedTiers[index];
                                              if (!field.options) {
                                                field.options = [];
                                              }
                                              field.options.push(`Option ${field.options.length + 1}`);
                                              setTicketTiers(updatedTiers);
                                            }}
                                            className="text-xs text-teal-600 hover:text-teal-700"
                                          >
                                            Add Option
                                          </button>
                                        </div>
                                        {field.options && field.options.map((option, optIndex) => (
                                          <div key={optIndex} className="flex items-center mb-1">
                                            <input
                                              type="text"
                                              value={option}
                                              onChange={(e) => {
                                                const updatedTiers = [...ticketTiers];
                                                const tier = updatedTiers[index];
                                                field.options[optIndex] = e.target.value;
                                                setTicketTiers(updatedTiers);
                                              }}
                                              className="flex-1 px-2 py-1 bg-white border rounded text-sm"
                                              placeholder={`Option ${optIndex + 1}`}
                                            />
                                            {field.options.length > 1 && (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const updatedTiers = [...ticketTiers];
                                                  const tier = updatedTiers[index];
                                                  field.options.splice(optIndex, 1);
                                                  setTicketTiers(updatedTiers);
                                                }}
                                                className="ml-2 text-red-500 hover:text-red-700 text-xs"
                                              >
                                                Remove
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {/* Conditional Fields for radio/select fields in conditional fields */}
                                    {(field.type === 'radio' || field.type === 'select') && (
                                      <div className="mt-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updatedTiers = [...ticketTiers];
                                            const tier = updatedTiers[index];
                                            if (!field.conditionalFields) {
                                              field.conditionalFields = {};
                                            }
                                            // Initialize conditional fields for each option
                                            if (field.options) {
                                              field.options.forEach(option => {
                                                if (!field.conditionalFields[option]) {
                                                  field.conditionalFields[option] = [];
                                                }
                                              });
                                            }
                                            setTicketTiers(updatedTiers);
                                          }}
                                          className="px-2 py-1 bg-purple-500 text-white rounded text-xs hover:bg-purple-600"
                                        >
                                          Add Nested Conditional Fields
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>

                              {ticketTiers.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeTicketTier(index)}
                                  className="mt-2 px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 transition-all"
                                >
                                  Remove Tier
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-gray-700" htmlFor="image">Event Image <span className="text-red-500">*</span></label>
                      <input
                        id="image"
                        type="file"
                        name="image"
                        accept="image/*"
                        onChange={handleMeetingChange}
                        className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm transition-all duration-300 hover:bg-gray-50"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700" htmlFor="maxTickets">Max Tickets per Booking</label>
                      <select
                        id="maxTickets"
                        value={maxTicketsPerBooking}
                        onChange={(e) => setMaxTicketsPerBooking(parseInt(e.target.value))}
                        className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                      >
                        <option value="1">Single (1 ticket)</option>
                        <option value="2">Multiple (2 tickets)</option>
                        <option value="3">Multiple (3 tickets)</option>
                        <option value="4">Multiple (4 tickets)</option>
                        <option value="5">Multiple (5 tickets)</option>
                        <option value="10">Multiple (10 tickets)</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700" htmlFor="description">Description <span className="text-red-500">*</span></label>
                <textarea
                  id="description"
                  name="description"
                  value={meetingData.description}
                  onChange={handleMeetingChange}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:bg-gray-50"
                  rows="2"
                  required
                  aria-required="true"
                />
              </div>

              {/* Form Builder Section for Public Events */}
              {meetingData.isPublic === 'public' && showFormBuilder && (
                <div className="border-t pt-4 mt-4 animate-fade-in">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-sm font-semibold text-gray-600">Custom Registration Form</h2>
                    <button
                      type="button"
                      onClick={() => setShowFormBuilder(!showFormBuilder)}
                      className="text-xs text-teal-600 hover:text-teal-700"
                    >
                      {showFormBuilder ? 'Hide Builder' : 'Show Builder'}
                    </button>
                  </div>

                  {showFormBuilder && (
                    <div className="space-y-4">
                      {/* Form Field Controls */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <button
                          type="button"
                          onClick={() => addFormField('text')}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs transition-all"
                        >
                          Text Field
                        </button>
                        <button
                          type="button"
                          onClick={() => addFormField('email')}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs transition-all"
                        >
                          Email
                        </button>
                        <button
                          type="button"
                          onClick={() => addFormField('tel')}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs transition-all"
                        >
                          Phone
                        </button>
                        <button
                          type="button"
                          onClick={() => addFormField('select')}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs transition-all"
                        >
                          Dropdown
                        </button>
                        <button
                          type="button"
                          onClick={() => addFormField('radio')}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs transition-all"
                        >
                          Radio Buttons
                        </button>
                        <button
                          type="button"
                          onClick={() => addFormField('checkbox')}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs transition-all"
                        >
                          Checkbox
                        </button>
                        <button
                          type="button"
                          onClick={() => addFormField('number')}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs transition-all"
                        >
                          Number
                        </button>
                        <button
                          type="button"
                          onClick={() => addFormField('date')}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs transition-all"
                        >
                          Date
                        </button>
                      </div>

                      {/* Form Fields List */}
                      <div className="space-y-3">
                        {customFormFields.map((field, index) => (
                          <div key={field.id} className="border rounded-lg p-3 bg-gray-50">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <input
                                  type="text"
                                  value={field.label}
                                  onChange={(e) => updateFormField(index, { label: e.target.value })}
                                  className="w-full px-2 py-1 bg-white border rounded text-sm mb-2"
                                  placeholder="Field Label"
                                />
                                {/* REMOVE this input for radio buttons */}
                                {field.type !== 'radio' && (
                                  <input
                                    type="text"
                                    value={field.placeholder}
                                    onChange={(e) => updateFormField(index, { placeholder: e.target.value })}
                                    className="w-full px-2 py-1 bg-white border rounded text-sm"
                                    placeholder="Placeholder Text"
                                  />
                                )}
                              </div>
                              <div className="ml-3 flex items-start space-x-2">
                                <label className="flex items-center text-xs">
                                  <input
                                    type="checkbox"
                                    checked={field.required}
                                    onChange={(e) => updateFormField(index, { required: e.target.checked })}
                                    className="mr-1"
                                  />
                                  Required
                                </label>
                                <button
                                  type="button"
                                  onClick={() => removeFormField(index)}
                                  className="text-red-500 hover:text-red-700 text-xs"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>

                            {/* Options for select/radio fields - SHOW RADIO BUTTONS FOR RADIO TYPE */}
                            {(field.type === 'select' || field.type === 'radio') && (
                              <div className="mt-2">
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-xs font-medium">Options:</span>
                                  <button
                                    type="button"
                                    onClick={() => addOptionToField(index)}
                                    className="text-xs text-teal-600 hover:text-teal-700"
                                  >
                                    Add Option
                                  </button>
                                </div>
                                {field.options.map((option, optIndex) => (
                                  <div key={optIndex} className="flex items-center mb-1">
                                    {/* For radio type, show a preview of the radio button */}
                                    {field.type === 'radio' && (
                                      <div className="mr-2 flex items-center">
                                        <input
                                          type="radio"
                                          name={`preview_${field.id}`}
                                          disabled
                                          className="mr-1"
                                        />
                                      </div>
                                    )}
                                    <input
                                      type="text"
                                      value={option}
                                      onChange={(e) => updateOptionInField(index, optIndex, e.target.value)}
                                      className="flex-1 px-2 py-1 bg-white border rounded text-sm"
                                      placeholder={`Option ${optIndex + 1}`}
                                    />
                                    {field.options.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => removeOptionFromField(index, optIndex)}
                                        className="ml-2 text-red-500 hover:text-red-700 text-xs"
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* PREVIEW OF HOW RADIO BUTTONS WILL LOOK */}
                            {field.type === 'radio' && field.options.length > 0 && (
                              <div className="mt-3 p-2 bg-white border rounded">
                                <span className="text-xs font-medium text-gray-600 mb-1 block">Preview:</span>
                                <div className="space-y-1">
                                  {field.options.map((option, optIndex) => (
                                    <label key={optIndex} className="flex items-center text-xs">
                                      <input
                                        type="radio"
                                        name={`builder_preview_${field.id}`}
                                        className="mr-2"
                                        disabled
                                      />
                                      {option}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Conditional Fields for select/radio fields */}
                            {(field.type === 'select' || field.type === 'radio') && (
                              <div className="mt-3">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-xs font-medium text-gray-600">Conditional Fields</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newConditionalFields = { ...(field.conditionalFields || {}) };
                                      // Add conditional fields for each option
                                      field.options.forEach(option => {
                                        if (!newConditionalFields[option]) {
                                          newConditionalFields[option] = [];
                                        }
                                      });
                                      updateFormField(index, { conditionalFields: newConditionalFields });
                                    }}
                                    className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-all"
                                  >
                                    Add Conditional Fields
                                  </button>
                                </div>
                                {field.conditionalFields && Object.entries(field.conditionalFields).map(([option, conditionalFields]) => (
                                  <div key={option} className="mb-2 p-2 bg-white border rounded">
                                    <div className="flex justify-between items-center mb-2">
                                      <span className="text-xs font-medium">When "{option}" is selected:</span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newConditionalFields = { ...field.conditionalFields };
                                          newConditionalFields[option] = [
                                            ...(newConditionalFields[option] || []),
                                            {
                                              id: Date.now() + Math.random(),
                                              type: 'text',
                                              label: 'Additional Field',
                                              placeholder: 'Enter information',
                                              required: false,
                                              fieldName: `conditional_${option.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`
                                            }
                                          ];
                                          updateFormField(index, { conditionalFields: newConditionalFields });
                                        }}
                                        className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                                      >
                                        Add Field
                                      </button>
                                    </div>
                                    {conditionalFields.map((condField, condIndex) => (
                                      <div key={condField.id} className="mb-2 p-2 bg-gray-50 border rounded">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                                          <div>
                                            <input
                                              type="text"
                                              value={condField.label}
                                              onChange={(e) => {
                                                const newConditionalFields = { ...field.conditionalFields };
                                                newConditionalFields[option][condIndex].label = e.target.value;
                                                updateFormField(index, { conditionalFields: newConditionalFields });
                                              }}
                                              className="w-full px-2 py-1 bg-white border rounded text-sm"
                                              placeholder="Field Label"
                                            />
                                          </div>
                                          <div className="flex items-center space-x-2">
                                            <select
                                              value={condField.type}
                                              onChange={(e) => {
                                                const newConditionalFields = { ...field.conditionalFields };
                                                newConditionalFields[option][condIndex].type = e.target.value;
                                                updateFormField(index, { conditionalFields: newConditionalFields });
                                              }}
                                              className="px-2 py-1 bg-white border rounded text-sm"
                                            >
                                              <option value="text">Text</option>
                                              <option value="email">Email</option>
                                              <option value="tel">Phone</option>
                                              <option value="number">Number</option>
                                              <option value="select">Dropdown</option>
                                              <option value="radio">Radio Buttons</option>
                                            </select>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const newConditionalFields = { ...field.conditionalFields };
                                                newConditionalFields[option].splice(condIndex, 1);
                                                updateFormField(index, { conditionalFields: newConditionalFields });
                                              }}
                                              className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                                            >
                                              Remove
                                            </button>
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <input
                                            type="text"
                                            value={condField.placeholder}
                                            onChange={(e) => {
                                              const newConditionalFields = { ...field.conditionalFields };
                                              newConditionalFields[option][condIndex].placeholder = e.target.value;
                                              updateFormField(index, { conditionalFields: newConditionalFields });
                                            }}
                                            className="flex-1 px-2 py-1 bg-white border rounded text-sm"
                                            placeholder="Placeholder"
                                          />
                                          <label className="flex items-center text-xs">
                                            <input
                                              type="checkbox"
                                              checked={condField.required}
                                              onChange={(e) => {
                                                const newConditionalFields = { ...field.conditionalFields };
                                                newConditionalFields[option][condIndex].required = e.target.checked;
                                                updateFormField(index, { conditionalFields: newConditionalFields });
                                              }}
                                              className="mr-1"
                                            />
                                            Required
                                          </label>
                                        </div>
                                        {/* Options for radio/select fields in conditional fields */}
                                        {(condField.type === 'radio' || condField.type === 'select') && (
                                          <div className="mt-2">
                                            <div className="flex justify-between items-center mb-1">
                                              <span className="text-xs font-medium">Options:</span>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const newConditionalFields = { ...field.conditionalFields };
                                                  if (!newConditionalFields[option][condIndex].options) {
                                                    newConditionalFields[option][condIndex].options = [];
                                                  }
                                                  newConditionalFields[option][condIndex].options.push(`Option ${newConditionalFields[option][condIndex].options.length + 1}`);
                                                  updateFormField(index, { conditionalFields: newConditionalFields });
                                                }}
                                                className="text-xs text-teal-600 hover:text-teal-700"
                                              >
                                                Add Option
                                              </button>
                                            </div>
                                            {condField.options && condField.options.map((optionValue, optIndex) => (
                                              <div key={optIndex} className="flex items-center mb-1">
                                                {/* Show radio button preview for radio type in conditional fields */}
                                                {condField.type === 'radio' && (
                                                  <div className="mr-2 flex items-center">
                                                    <input
                                                      type="radio"
                                                      name={`cond_preview_${condField.id}`}
                                                      disabled
                                                      className="mr-1"
                                                    />
                                                  </div>
                                                )}
                                                <input
                                                  type="text"
                                                  value={optionValue}
                                                  onChange={(e) => {
                                                    const newConditionalFields = { ...field.conditionalFields };
                                                    newConditionalFields[option][condIndex].options[optIndex] = e.target.value;
                                                    updateFormField(index, { conditionalFields: newConditionalFields });
                                                  }}
                                                  className="flex-1 px-2 py-1 bg-white border rounded text-sm"
                                                  placeholder={`Option ${optIndex + 1}`}
                                                />
                                                {condField.options.length > 1 && (
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const newConditionalFields = { ...field.conditionalFields };
                                                      newConditionalFields[option][condIndex].options.splice(optIndex, 1);
                                                      updateFormField(index, { conditionalFields: newConditionalFields });
                                                    }}
                                                    className="ml-2 text-red-500 hover:text-red-700 text-xs"
                                                  >
                                                    Remove
                                                  </button>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        {/* Nested conditional fields for radio/select in conditional fields */}
                                        {(condField.type === 'radio' || condField.type === 'select') && (
                                          <div className="mt-2">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const newConditionalFields = { ...field.conditionalFields };
                                                if (!newConditionalFields[option][condIndex].conditionalFields) {
                                                  newConditionalFields[option][condIndex].conditionalFields = {};
                                                }
                                                // Initialize nested conditional fields for each option
                                                if (condField.options) {
                                                  condField.options.forEach(nestedOption => {
                                                    if (!newConditionalFields[option][condIndex].conditionalFields[nestedOption]) {
                                                      newConditionalFields[option][condIndex].conditionalFields[nestedOption] = [];
                                                    }
                                                  });
                                                }
                                                updateFormField(index, { conditionalFields: newConditionalFields });
                                              }}
                                              className="px-2 py-1 bg-purple-500 text-white rounded text-xs hover:bg-purple-600"
                                            >
                                              Add Nested Conditional Fields
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setInputMode('manual')}
                  className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 ${inputMode === 'manual' ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  aria-label="Switch to Manual Input"
                >
                  Manual Input
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode('csv')}
                  className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 ${inputMode === 'csv' ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  aria-label="Switch to CSV Upload"
                >
                  CSV Upload
                </button>
              </div>

              {(inputMode === 'manual' || meetingData.isPublic === 'public') && (
                <div className="animate-fade-in">
                  <h2 className="text-sm font-semibold text-gray-600 mb-3">Add Attendees (Optional)</h2>
                  {attendees.map((attendee, index) => (
                    <div key={index} className="space-y-2 border p-3 rounded-lg mb-3 bg-gray-50 transition-all duration-300 hover:shadow-md">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600" htmlFor={`attendee-name-${index}`}>Name</label>
                          <input
                            id={`attendee-name-${index}`}
                            type="text"
                            name="name"
                            value={attendee.name}
                            onChange={(e) => handleAttendeeChange(index, e)}
                            className="w-full px-2 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-200 hover:bg-gray-50"
                            aria-label={`Attendee ${index + 1} Name`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600" htmlFor={`attendee-email-${index}`}>Email</label>
                          <input
                            id={`attendee-email-${index}`}
                            type="email"
                            name="email"
                            value={attendee.email}
                            onChange={(e) => handleAttendeeChange(index, e)}
                            className="w-full px-2 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-200 hover:bg-gray-50"
                            aria-label={`Attendee ${index + 1} Email`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600" htmlFor={`attendee-phone-${index}`}>Phone</label>
                          <input
                            id={`attendee-phone-${index}`}
                            type="tel"
                            name="phone"
                            value={attendee.phone}
                            onChange={(e) => handleAttendeeChange(index, e)}
                            className="w-full px-2 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-200 hover:bg-gray-50"
                            aria-label={`Attendee ${index + 1} Phone`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600" htmlFor={`attendee-role-${index}`}>Role</label>
                          <select
                            id={`attendee-role-${index}`}
                            name="role"
                            value={attendee.role}
                            onChange={(e) => handleAttendeeChange(index, e)}
                            className="w-full px-2 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-200 hover:bg-gray-50"
                            aria-label={`Attendee ${index + 1} Role`}
                          >
                            <option value="">Select Role</option>
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                            <option value="supervisor">Supervisor</option>
                          </select>
                        </div>
                      </div>
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => removeAttendee(index)}
                          className="mt-2 px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 text-xs sm:text-sm transform hover:shadow-sm"
                          aria-label={`Remove Attendee ${index + 1}`}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addAttendee}
                    className="px-3 py-1.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-all duration-200 text-sm transform hover:shadow-sm"
                    aria-label="Add New Attendee"
                  >
                    Add Attendee
                  </button>
                </div>
              )}

              {inputMode === 'csv' && (
                <div className="border-t pt-3 animate-fade-in">
                  <h2 className="text-sm font-semibold text-gray-600 mb-2">Upload Attendees via CSV (Optional)</h2>
                  <p className="text-gray-600 text-xs mb-2">Download the template, fill it with Name, Email, Phone, and Role (user, admin, supervisor), then upload.</p>
                  <button
                    type="button"
                    onClick={downloadTemplate}
                    className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 text-sm transform hover:shadow-sm"
                    aria-label="Download CSV Template"
                  >
                    Download CSV Template
                  </button>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="mt-3 block w-full text-xs sm:text-sm text-gray-600 bg-white border border-gray-300 rounded-lg file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs sm:file:text-sm file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 transition-all duration-200"
                    aria-label="Upload CSV File"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-2 px-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all duration-200 shadow-sm text-sm sm:text-base ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-md'
                  }`}
                aria-label={isSubmitting ? 'Creating Meeting...' : 'Create Meeting'}
              >
                {isSubmitting ? 'Creating Meeting...' : 'Create Meeting'}
              </button>
            </form>
          </div>

          {/* Mobile Preview - Right Side */}
          {meetingData.isPublic === 'public' && customFormFields.length > 0 && (
            <div className="lg:w-1/3">
              <div className="bg-white p-4 rounded-xl shadow-lg sticky top-24">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Mobile Preview</h3>
                <div className="border-2 border-gray-300 rounded-2xl p-4 max-w-xs mx-auto bg-gray-50">
                  {/* Mock Mobile Screen */}
                  <div className="flex justify-center mb-3">
                    <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-center text-gray-800">{meetingData.title || 'Event Title'}</h4>

                    {/* Ticket Quantity Selector */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Number of Tickets</label>
                      <select
                        className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                        onChange={(e) => handlePreviewChange('ticketQuantity', e.target.value)}
                      >
                        {[...Array(maxTicketsPerBooking)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1} ticket{i !== 0 ? 's' : ''}</option>
                        ))}
                      </select>
                    </div>

                    {/* Ticket Type Selection with Price Display */}
                    {meetingData.priceType === 'tiered' && (
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Select Ticket Type</label>
                        <div className="space-y-2">
                          {ticketTiers.map((tier) => (
                            <div key={tier.id} className="flex items-center justify-between p-2 border rounded bg-white">
                              <div>
                                <div className="flex items-center">
                                  <input
                                    type="radio"
                                    name="preview_ticket_type"
                                    value={tier.name}
                                    checked={previewFormData.ticket_type === tier.name}
                                    onChange={(e) => handlePreviewChange('ticket_type', e.target.value)}
                                    className="mr-2"
                                  />
                                  <span className="text-xs font-medium">{tier.name}</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{tier.description}</p>
                              </div>
                              <span className="text-xs font-bold text-teal-600">₹{tier.price || '0'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Custom Form Preview */}
                    {customFormFields.map((field, index) => {
                      const fieldValue = previewFormData[field.fieldName] || '';
                      const conditionalFields = getConditionalFields(field, fieldValue);

                      return (
                        <div key={index} className="mb-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                          </label>

                          {field.type === 'text' && (
                            <input
                              type="text"
                              placeholder={field.placeholder}
                              className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                              onChange={(e) => handlePreviewChange(field.fieldName, e.target.value)}
                            />
                          )}

                          {field.type === 'email' && (
                            <input
                              type="email"
                              placeholder={field.placeholder}
                              className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                              onChange={(e) => handlePreviewChange(field.fieldName, e.target.value)}
                            />
                          )}

                          {field.type === 'tel' && (
                            <input
                              type="tel"
                              placeholder={field.placeholder}
                              className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                              onChange={(e) => handlePreviewChange(field.fieldName, e.target.value)}
                            />
                          )}

                          {field.type === 'select' && (
                            <select
                              className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                              onChange={(e) => handlePreviewChange(field.fieldName, e.target.value)}
                              value={fieldValue}
                            >
                              <option value="">Select {field.label}</option>
                              {field.options.map((option, optIndex) => (
                                <option key={optIndex} value={option}>{option}</option>
                              ))}
                            </select>
                          )}

                          {field.type === 'radio' && (
                            <div className="space-y-1">
                              {field.options.map((option, optIndex) => (
                                <label key={optIndex} className="flex items-center text-xs">
                                  <input
                                    type="radio"
                                    name={field.fieldName}
                                    value={option}
                                    checked={fieldValue === option}
                                    className="mr-2"
                                    onChange={(e) => handlePreviewChange(field.fieldName, e.target.value)}
                                  />
                                  {option}
                                </label>
                              ))}
                            </div>
                          )}

                          {field.type === 'checkbox' && (
                            <label className="flex items-center text-xs">
                              <input
                                type="checkbox"
                                className="mr-2"
                                onChange={(e) => handlePreviewChange(field.fieldName, e.target.checked)}
                              />
                              {field.label}
                            </label>
                          )}

                          {field.type === 'number' && (
                            <input
                              type="number"
                              placeholder={field.placeholder}
                              className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                              onChange={(e) => handlePreviewChange(field.fieldName, e.target.value)}
                            />
                          )}

                          {field.type === 'date' && (
                            <input
                              type="date"
                              className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                              onChange={(e) => handlePreviewChange(field.fieldName, e.target.value)}
                            />
                          )}

                          {field.type === 'textarea' && (
                            <textarea
                              placeholder={field.placeholder}
                              rows="2"
                              className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                              onChange={(e) => handlePreviewChange(field.fieldName, e.target.value)}
                            />
                          )}

                          {/* Show conditional fields */}
                          {conditionalFields.length > 0 && (
                            <div className="mt-2 ml-4 border-l-2 border-teal-200 pl-2">
                              {conditionalFields.map((condField, condIndex) => {
                                const condFieldValue = previewFormData[condField.fieldName] || '';
                                const nestedConditionalFields = (condField.type === 'radio' || condField.type === 'select')
                                  ? getNestedConditionalFields(field, fieldValue, condField.fieldName, condFieldValue)
                                  : [];

                                return (
                                  <div key={condIndex} className="mb-2">
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      {condField.label}
                                      {condField.required && <span className="text-red-500 ml-1">*</span>}
                                    </label>

                                    {condField.type === 'text' && (
                                      <input
                                        type="text"
                                        placeholder={condField.placeholder}
                                        className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                                        onChange={(e) => handlePreviewChange(condField.fieldName, e.target.value)}
                                      />
                                    )}

                                    {condField.type === 'email' && (
                                      <input
                                        type="email"
                                        placeholder={condField.placeholder}
                                        className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                                        onChange={(e) => handlePreviewChange(condField.fieldName, e.target.value)}
                                      />
                                    )}

                                    {condField.type === 'tel' && (
                                      <input
                                        type="tel"
                                        placeholder={condField.placeholder}
                                        className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                                        onChange={(e) => handlePreviewChange(condField.fieldName, e.target.value)}
                                      />
                                    )}

                                    {condField.type === 'select' && (
                                      <select
                                        className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                                        onChange={(e) => handlePreviewChange(condField.fieldName, e.target.value)}
                                        value={condFieldValue}
                                      >
                                        <option value="">Select {condField.label}</option>
                                        {condField.options && condField.options.map((option, optIndex) => (
                                          <option key={optIndex} value={option}>{option}</option>
                                        ))}
                                      </select>
                                    )}

                                    {condField.type === 'radio' && (
                                      <div className="space-y-1">
                                        {condField.options && condField.options.map((option, optIndex) => (
                                          <label key={optIndex} className="flex items-center text-xs">
                                            <input
                                              type="radio"
                                              name={condField.fieldName}
                                              value={option}
                                              checked={condFieldValue === option}
                                              className="mr-2"
                                              onChange={(e) => handlePreviewChange(condField.fieldName, e.target.value)}
                                            />
                                            {option}
                                          </label>
                                        ))}
                                      </div>
                                    )}

                                    {condField.type === 'number' && (
                                      <input
                                        type="number"
                                        placeholder={condField.placeholder}
                                        className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                                        onChange={(e) => handlePreviewChange(condField.fieldName, e.target.value)}
                                      />
                                    )}

                                    {condField.type === 'date' && (
                                      <input
                                        type="date"
                                        className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                                        onChange={(e) => handlePreviewChange(condField.fieldName, e.target.value)}
                                      />
                                    )}

                                    {/* Show nested conditional fields for radio/select */}
                                    {nestedConditionalFields.length > 0 && (
                                      <div className="mt-2 ml-4 border-l-2 border-blue-200 pl-2">
                                        {nestedConditionalFields.map((nestedField, nestedIndex) => (
                                          <div key={nestedIndex} className="mb-2">
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              {nestedField.label}
                                              {nestedField.required && <span className="text-red-500 ml-1">*</span>}
                                            </label>
                                            {nestedField.type === 'text' && (
                                              <input
                                                type="text"
                                                placeholder={nestedField.placeholder}
                                                className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                                                onChange={(e) => handlePreviewChange(nestedField.fieldName, e.target.value)}
                                              />
                                            )}
                                            {nestedField.type === 'select' && (
                                              <select
                                                className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                                                onChange={(e) => handlePreviewChange(nestedField.fieldName, e.target.value)}
                                              >
                                                <option value="">Select {nestedField.label}</option>
                                                {nestedField.options && nestedField.options.map((option, optIndex) => (
                                                  <option key={optIndex} value={option}>{option}</option>
                                                ))}
                                              </select>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Price Summary */}
                    <div className="border-t pt-2 mt-3">
                      <div className="flex justify-between text-xs">
                        <span>Subtotal:</span>
                        <span className="font-medium">
                          {meetingData.priceType === 'single'
                            ? `₹${meetingData.price || '0'}`
                            : previewFormData.ticket_type
                              ? `₹${ticketTiers.find(t => t.name === previewFormData.ticket_type)?.price || '0'}`
                              : 'Select ticket type'
                          }
                        </span>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      className="w-full py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors mt-3"
                    >
                      Book Now
                    </button>
                  </div>
                </div>

                <div className="mt-3 text-xs text-gray-500">
                  <p>This is how your registration form will appear to attendees on mobile devices.</p>
                  <p className="mt-1"><strong>Fields in form:</strong> {customFormFields.length}</p>
                  <p><strong>Required fields:</strong> {customFormFields.filter(f => f.required).length}</p>
                  <p><strong>Ticket tiers:</strong> {ticketTiers.length}</p>
                  <p><strong>Conditional logic:</strong> {customFormFields.filter(f => f.conditionalFields).length} field(s) with conditions</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showConfirmPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white p-5 rounded-xl shadow-lg max-w-2xl w-full max-h-[70vh] overflow-y-auto transform scale-95 animate-scale-in">
            <h2 className="text-sm font-bold mb-3 text-gray-800">Confirm Meeting and Attendees</h2>
            <div className="mb-4">
              <h3 className="text-xs font-semibold mb-2 text-gray-700">Meeting Details</h3>
              <ul className="list-disc pl-4 text-xs text-gray-600">
                <li><strong>Title:</strong> {meetingData.title}</li>
                <li><strong>Location:</strong> {meetingData.location}</li>
                <li><strong>Time:</strong> {new Date(meetingData.time).toLocaleString()}</li>
                <li><strong>Description:</strong> {meetingData.description}</li>
                <li><strong>Purpose:</strong> {meetingData.purpose}</li>
                <li><strong>Attire:</strong> {meetingData.attire}</li>
                <li><strong>Company:</strong> {meetingData.company}</li>
                <li><strong>Type:</strong> {meetingData.isPublic === 'public' ? 'Public' : 'Private'}</li>
                {meetingData.isPublic === 'public' && (
                  <>
                    <li><strong>Capacity:</strong> {meetingData.capacity}</li>
                    <li><strong>Pricing Type:</strong> {meetingData.priceType}</li>
                    {meetingData.priceType === 'single' ? (
                      <li><strong>Ticket Price:</strong> ₹{meetingData.price || '0'}</li>
                    ) : (
                      <>
                        <li><strong>Ticket Tiers:</strong></li>
                        {ticketTiers.map((tier) => (
                          <li key={tier.id} className="ml-4">
                            {tier.name}: ₹{tier.price || '0'} - {tier.description}
                          </li>
                        ))}
                      </>
                    )}
                    <li><strong>Max tickets per booking:</strong> {maxTicketsPerBooking}</li>
                    <li><strong>Custom Form Fields:</strong> {customFormFields.length}</li>
                    <li><strong>Conditional Fields:</strong> {customFormFields.filter(f => f.conditionalFields).length}</li>
                    <li><strong>Image:</strong> {meetingData.image ? meetingData.image.name : 'No image'}</li>
                  </>
                )}
              </ul>
            </div>
            <h3 className="text-xs font-semibold mb-2 text-gray-700">Attendees</h3>
            {confirmPreview && confirmPreview.length > 0 ? (
              <table className="w-full border-collapse text-xs sm:text-sm">
                <thead>
                  <tr className="bg-teal-50">
                    <th className="border border-gray-200 p-2 text-left font-medium text-gray-700">Name</th>
                    <th className="border border-gray-200 p-2 text-left font-medium text-gray-700">Email</th>
                    <th className="border border-gray-200 p-2 text-left font-medium text-gray-700">Phone</th>
                    <th className="border border-gray-200 p-2 text-left font-medium text-gray-700">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {confirmPreview.map((attendee, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-all duration-200">
                      <td className="border border-gray-200 p-2 text-gray-600">{attendee.name}</td>
                      <td className="border border-gray-200 p-2 text-gray-600">{attendee.email}</td>
                      <td className="border border-gray-200 p-2 text-gray-600">{attendee.phone}</td>
                      <td className="border border-gray-200 p-2 text-gray-600">{attendee.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-xs sm:text-sm text-gray-600">
                No attendees. {meetingData.isPublic === 'public' ? '' : 'Creator will be added automatically.'}
              </p>
            )}
            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowConfirmPopup(false);
                  setCsvFile(null);
                  setConfirmPreview(null);
                }}
                className="px-3 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 text-xs sm:text-sm transform hover:shadow-sm"
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmSubmit}
                className="px-3 py-1.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-all duration-200 text-xs sm:text-sm transform hover:shadow-sm"
                aria-label="Confirm and Submit"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreditPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white p-5 rounded-lg shadow-lg max-w-md w-full transform scale-95 animate-scale-in">
            <h2 className="text-sm font-semibold mb-2 text-gray-800">Add Credits</h2>
            <p className="text-xs text-gray-500 mb-3">Minimum purchase: ₹100 (100 credits). 1 credit = 1 invite.</p>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="current-credits">
                Current Credits
              </label>
              <div
                id="current-credits"
                className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm font-medium text-gray-600"
              >
                {credits} credits
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="credit-amount">
                Amount to Add (₹)
              </label>
              <input
                id="credit-amount"
                type="number"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-200 hover:bg-gray-50"
                min="100"
                required
                aria-required="true"
                aria-label="Amount to Add in Rupees"
              />
            </div>
            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowCreditPopup(false);
                  setCreditAmount('');
                }}
                className="px-3 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 text-xs sm:text-sm transform hover:shadow-sm"
                aria-label="Cancel Add Credits"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCredits}
                disabled={isSubmitting}
                className={`px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 text-xs sm:text-sm transform hover:shadow-sm ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                aria-label={isSubmitting ? 'Processing Credit Purchase' : 'Proceed to Payment'}
              >
                {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateMeeting;